import { useCallback, useEffect, useState } from 'react';
import {
  View,
  Text,
  Pressable,
  ActivityIndicator,
  StyleSheet,
  Platform,
} from 'react-native';
import { doc, getDoc } from 'firebase/firestore';
import { Ionicons, MaterialIcons } from '@expo/vector-icons';
import { firestore } from '../../services/firebase';
import AdminInput from '../AdminInput';
import AdminSwitch from '../AdminSwitch';
import { ADMIN_COLORS, ADMIN_FONT } from '../../config/theme/adminColors';
import { Badge, SectionCard } from '../../pages/AdminPanel/Products/formStyles';
import { useRemoteConfigContext } from '../../contexts/useRemoteConfigContext';
import {
  deleteProductCatalogFilter,
  loadProductCatalogFilterTree,
  loadProductCatalogFilters,
  reorderProductCatalogFilters,
  saveProductCatalogFilter,
  setProductCatalogFilterArchived,
} from '../../pages/AdminPanel/Products/service/product-catalog-filters-service';
import {
  copyTextToClipboard,
  buildProductCatalogAiAssistantText,
} from '../../utils/formatProductCatalogStructureForCopy';

export default function ProductCatalogFilters({
  collectionName,
  docId,
  accessIds = [],
  productName = '',
}) {
  const { hasProductCatalog } = useRemoteConfigContext();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [filtersList, setFiltersList] = useState([]);
  const [localFiltersList, setLocalFiltersList] = useState(null);
  const [dragState, setDragState] = useState({ from: null, over: null });
  const [reordering, setReordering] = useState(false);
  const [tree, setTree] = useState([]);
  const [expandedFilterId, setExpandedFilterId] = useState(null);
  const [expandedCategoryIds, setExpandedCategoryIds] = useState([]);
  const [showCreate, setShowCreate] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingFilterId, setEditingFilterId] = useState(null);
  const [newName, setNewName] = useState({ value: '', error: '' });
  const [formError, setFormError] = useState('');
  const [associatedCats, setAssociatedCats] = useState([]);
  const [associatedCards, setAssociatedCards] = useState([]);
  const [aiPromptFeedback, setAiPromptFeedback] = useState('');

  const canManage = hasProductCatalog && collectionName && docId && accessIds.length > 0;
  const canDragFilters = Platform.OS === 'web' && !showCreate && !isEditing && !reordering;
  const displayFilters = localFiltersList ?? filtersList;

  const reloadFilters = useCallback(async () => {
    if (!canManage) return;
    const filters = await loadProductCatalogFilters(collectionName, docId);
    setFiltersList(filters);
    setLocalFiltersList(null);
  }, [canManage, collectionName, docId]);

  useEffect(() => {
    setLocalFiltersList(null);
  }, [filtersList]);

  const reloadTree = useCallback(async () => {
    if (!canManage) return;
    const nextTree = await loadProductCatalogFilterTree(accessIds);
    setTree(nextTree);
  }, [canManage, accessIds]);

  useEffect(() => {
    if (!canManage) {
      setLoading(false);
      return;
    }

    (async () => {
      setLoading(true);
      try {
        await Promise.all([reloadFilters(), reloadTree()]);
      } catch (error) {
        console.error('Erro ao carregar filtros do produto:', error);
      } finally {
        setLoading(false);
      }
    })();
  }, [canManage, reloadFilters, reloadTree]);

  useEffect(() => {
    if (!expandedFilterId) {
      setAssociatedCats([]);
      setAssociatedCards([]);
      return;
    }

    const filter = filtersList.find((item) => item.id === expandedFilterId);
    if (!filter) return;

    (async () => {
      const cats = await Promise.all(
        (filter.categoryIds || []).map(async (catId) => {
          const cSnap = await getDoc(doc(firestore, 'categories', catId));
          return { id: catId, title: cSnap.data()?.title ?? '' };
        })
      );
      setAssociatedCats(cats);

      const cards = await Promise.all(
        (filter.cardEntries || []).map(async ({ categoryId, cardId }) => {
          const cardSnap = await getDoc(
            doc(firestore, 'categories', categoryId, 'cards', cardId)
          );
          return {
            categoryId,
            cardId,
            title: cardSnap.data()?.title ?? '',
          };
        })
      );
      setAssociatedCards(cards);
    })();
  }, [expandedFilterId, filtersList]);

  if (!hasProductCatalog) return null;
  if (!docId) return null;

  const resetForm = () => {
    setNewName({ value: '', error: '' });
    setFormError('');
    setShowCreate(false);
    setIsEditing(false);
    setEditingFilterId(null);
    setTree((current) =>
      current.map((cat) => ({
        ...cat,
        selected: false,
        cards: cat.cards.map((card) => ({ ...card, selected: false })),
      }))
    );
  };

  const openCreateForm = () => {
    resetForm();
    setShowCreate(true);
  };

  const openEditForm = (filter) => {
    setShowCreate(false);
    setIsEditing(true);
    setEditingFilterId(filter.id);
    setExpandedFilterId(null);
    setNewName({ value: filter.name, error: '' });
    setFormError('');

    setTree((current) =>
      current.map((cat) => {
        const isCatSelected = (filter.categoryIds || []).includes(cat.id);
        return {
          ...cat,
          selected: isCatSelected,
          cards: cat.cards.map((card) => {
            const isCardSelected = (filter.cardEntries || []).some(
              (entry) => entry.categoryId === cat.id && entry.cardId === card.id
            );
            return {
              ...card,
              selected: isCatSelected ? true : isCardSelected,
            };
          }),
        };
      })
    );
  };

  const handleSave = async () => {
    setFormError('');
    setNewName((current) => ({ ...current, error: '' }));

    const selectedCategoryIds = tree.filter((cat) => cat.selected).map((cat) => cat.id);
    const selectedCardEntries = tree.flatMap((cat) =>
      cat.selected
        ? []
        : cat.cards
            .filter((card) => card.selected)
            .map((card) => ({ categoryId: cat.id, cardId: card.id }))
    );

    if (selectedCategoryIds.length === 0 && selectedCardEntries.length === 0) {
      setFormError('Selecione ao menos uma categoria ou módulo deste produto.');
      return;
    }

    setSaving(true);
    try {
      const editingFilter = filtersList.find((item) => item.id === editingFilterId);
      await saveProductCatalogFilter({
        collectionName,
        docId,
        filterId: isEditing ? editingFilterId : null,
        name: newName.value,
        order: isEditing ? editingFilter?.order : undefined,
        categoryIds: selectedCategoryIds,
        cardEntries: selectedCardEntries,
      });
      await reloadFilters();
      resetForm();
    } catch (error) {
      const message = error?.message || 'Não foi possível salvar o filtro.';
      if (message.includes('palavras') || message.includes('nome')) {
        setNewName((current) => ({ ...current, error: message }));
      } else {
        setFormError(message);
      }
    } finally {
      setSaving(false);
    }
  };

  const persistFilterOrder = async (orderedFilters) => {
    setReordering(true);
    try {
      await reorderProductCatalogFilters(collectionName, docId, orderedFilters);
      await reloadFilters();
    } catch (error) {
      console.error('Erro ao salvar ordem dos filtros:', error);
      setLocalFiltersList(null);
    } finally {
      setReordering(false);
    }
  };

  const handleFilterDrop = (from, to) => {
    setDragState({ from: null, over: null });
    if (from == null || to == null || from === to) return;

    const reordered = [...displayFilters];
    const [moved] = reordered.splice(from, 1);
    reordered.splice(to, 0, moved);
    setLocalFiltersList(reordered);
    persistFilterOrder(reordered);
  };

  const handleToggleArchive = async (filter) => {
    try {
      await setProductCatalogFilterArchived(
        collectionName,
        docId,
        filter.id,
        !filter.archived
      );
      await reloadFilters();
    } catch (error) {
      console.error('Erro ao arquivar filtro:', error);
    }
  };

  const handleDelete = async (filterId) => {
    if (!window.confirm('Excluir este filtro deste produto?')) return;

    try {
      await deleteProductCatalogFilter(collectionName, docId, filterId);
      if (expandedFilterId === filterId) setExpandedFilterId(null);
      await reloadFilters();
    } catch (error) {
      console.error('Erro ao excluir filtro:', error);
    }
  };

  const handleGenerateWithAi = async () => {
    if (!tree.length) return;

    const text = buildProductCatalogAiAssistantText(tree, { productName });
    try {
      const copied = await copyTextToClipboard(text);
      if (copied) {
        setAiPromptFeedback('Prompt copiado! Cole na IA da sua preferência (ChatGPT, Claude ou similar).');
      } else {
        setAiPromptFeedback('Não foi possível copiar. Tente novamente no navegador.');
      }
    } catch (error) {
      console.error('Erro ao copiar prompt para IA:', error);
      setAiPromptFeedback('Não foi possível copiar. Tente novamente no navegador.');
    }

    setTimeout(() => setAiPromptFeedback(''), 5000);
  };

  const generateWithAiButton =
    !loading && canManage && tree.length > 0 ? (
      <Pressable
        onPress={handleGenerateWithAi}
        style={({ hovered }) => [
          styles.generateWithAiBtn,
          hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
        ]}
      >
        <MaterialIcons name="auto-awesome" size={16} color={ADMIN_COLORS.accent} />
        <Text style={styles.generateWithAiBtnText}>Gerar com IA</Text>
      </Pressable>
    ) : null;

  const renderTree = () => {
    if (tree.length === 0) {
      return (
        <Text style={styles.emptyTree}>
          Vincule categorias ou módulos a este produto acima para criar filtros.
        </Text>
      );
    }

    return tree.map((cat) => {
      const isExpanded = expandedCategoryIds.includes(cat.id);
      return (
        <View key={cat.id}>
          <View style={styles.treeRow}>
            <Pressable
              hitSlop={6}
              style={styles.treeChevron}
              onPress={() =>
                setExpandedCategoryIds((prev) =>
                  prev.includes(cat.id)
                    ? prev.filter((id) => id !== cat.id)
                    : [...prev, cat.id]
                )
              }
            >
              <Ionicons
                name={isExpanded ? 'chevron-down' : 'chevron-forward'}
                size={16}
                color={ADMIN_COLORS.muted}
              />
            </Pressable>
            <Pressable
              style={({ hovered }) => [styles.treeLabelBtn, hovered && { opacity: 0.75 }]}
              onPress={() =>
                setTree((current) =>
                  current.map((item) =>
                    item.id === cat.id
                      ? {
                          ...item,
                          selected: !item.selected,
                          cards: item.cards.map((card) => ({
                            ...card,
                            selected: !item.selected,
                          })),
                        }
                      : item
                  )
                )
              }
            >
              <Ionicons
                name={cat.selected ? 'checkbox' : 'square-outline'}
                size={18}
                color={cat.selected ? ADMIN_COLORS.accent : ADMIN_COLORS.placeholder}
              />
              <Text style={styles.treeLabel} numberOfLines={1}>
                {cat.title}
              </Text>
            </Pressable>
          </View>

          {isExpanded &&
            cat.cards.map((card) => (
              <Pressable
                key={card.id}
                style={({ hovered }) => [
                  styles.treeLabelBtn,
                  styles.treeCardRow,
                  hovered && { opacity: 0.75 },
                ]}
                onPress={() =>
                  setTree((current) =>
                    current.map((item) =>
                      item.id === cat.id
                        ? {
                            ...item,
                            cards: item.cards.map((cd) =>
                              cd.id === card.id ? { ...cd, selected: !cd.selected } : cd
                            ),
                          }
                        : item
                    )
                  )
                }
              >
                <Ionicons
                  name={cat.selected || card.selected ? 'checkbox' : 'square-outline'}
                  size={18}
                  color={
                    cat.selected || card.selected
                      ? ADMIN_COLORS.accent
                      : ADMIN_COLORS.placeholder
                  }
                />
                <Text style={styles.treeLabel} numberOfLines={1}>
                  {card.title}
                </Text>
              </Pressable>
            ))}
        </View>
      );
    });
  };

  return (
    <SectionCard
      title="Filtros deste produto"
      subtitle="Tags para o aluno filtrar módulos dentro deste produto. Use Gerar com IA para copiar a estrutura do produto e um prompt pronto para colar na IA da sua preferência (ChatGPT, Claude ou similar) e receber sugestões de filtros, com categorias e módulos a marcar aqui. Arraste para reordenar."
      right={generateWithAiButton}
      style={{ marginTop: 16 }}
    >
      {aiPromptFeedback ? <Text style={styles.aiPromptFeedback}>{aiPromptFeedback}</Text> : null}
      {loading ? (
        <View style={styles.loadingBox}>
          <ActivityIndicator size="small" color={ADMIN_COLORS.accent} />
        </View>
      ) : (
        <>
          {displayFilters.length > 0 && (
            <View style={styles.listBox}>
              {displayFilters.map((filter, index) => {
                const isExpanded = expandedFilterId === filter.id;
                const isPublished = !filter.archived;
                const isDragging = dragState.from === index;
                const isDropTarget =
                  dragState.from !== null &&
                  dragState.over === index &&
                  dragState.from !== index;
                const showTopBar = isDropTarget && dragState.from > index;
                const showBottomBar = isDropTarget && dragState.from < index;

                const rowContent = (
                  <>
                    <View style={styles.filterRow}>
                      {canDragFilters && (
                        <div
                          draggable
                          onDragStart={(e) => {
                            e.dataTransfer.effectAllowed = 'move';
                            e.dataTransfer.setData('text/plain', String(index));
                            setDragState({ from: index, over: null });
                          }}
                          onDragEnd={() => setDragState({ from: null, over: null })}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            padding: 4,
                            marginRight: 4,
                            cursor: 'grab',
                            opacity: isDragging ? 0.35 : 1,
                          }}
                        >
                          <Ionicons name="reorder-three" size={20} color={ADMIN_COLORS.muted} />
                        </div>
                      )}

                      <Pressable
                        onPress={() => {
                          if (isExpanded) {
                            setExpandedFilterId(null);
                          } else {
                            setExpandedFilterId(filter.id);
                          }
                        }}
                        style={({ hovered }) => [
                          styles.filterRowMain,
                          hovered && { opacity: 0.7 },
                        ]}
                      >
                        <Ionicons
                          name={isExpanded ? 'chevron-down' : 'chevron-forward'}
                          size={16}
                          color={ADMIN_COLORS.muted}
                        />
                        <Text style={styles.filterName} numberOfLines={1}>
                          {filter.name}
                        </Text>
                      </Pressable>

                      <Badge variant={isPublished ? 'success' : 'neutral'}>
                        {isPublished ? 'Publicado' : 'Arquivado'}
                      </Badge>

                      <AdminSwitch
                        value={isPublished}
                        onValueChange={() => handleToggleArchive(filter)}
                      />
                    </View>

                    {isExpanded && (
                      <View style={styles.expanded}>
                        <Text style={styles.expandedLabel}>Conteúdos vinculados</Text>
                        {associatedCats.length === 0 && associatedCards.length === 0 ? (
                          <Text style={styles.expandedEmpty}>
                            Nenhum módulo vinculado a este filtro.
                          </Text>
                        ) : null}
                        {associatedCats.map((cat) => (
                          <Text style={styles.expandedItem} key={cat.id}>
                            • Categoria: {cat.title}
                          </Text>
                        ))}
                        {associatedCards.map((card) => (
                          <Text style={styles.expandedItem} key={`${card.categoryId}-${card.cardId}`}>
                            • Módulo: {card.title}
                          </Text>
                        ))}

                        <View style={styles.expandedActions}>
                          <Pressable
                            onPress={() => openEditForm(filter)}
                            style={({ hovered }) => [
                              styles.ghostBtn,
                              hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                            ]}
                          >
                            <Ionicons name="create-outline" size={16} color={ADMIN_COLORS.ink} />
                            <Text style={styles.ghostBtnText}>Editar</Text>
                          </Pressable>
                          <Pressable
                            onPress={() => handleDelete(filter.id)}
                            style={({ hovered }) => [
                              styles.ghostBtn,
                              { borderColor: ADMIN_COLORS.dangerBorder },
                              hovered && { backgroundColor: ADMIN_COLORS.dangerSoft },
                            ]}
                          >
                            <Ionicons name="trash-outline" size={16} color={ADMIN_COLORS.danger} />
                            <Text style={[styles.ghostBtnText, { color: ADMIN_COLORS.danger }]}>
                              Excluir
                            </Text>
                          </Pressable>
                        </View>
                      </View>
                    )}
                  </>
                );

                if (!canDragFilters) {
                  return (
                    <View
                      key={filter.id}
                      style={index < displayFilters.length - 1 ? styles.listDivider : null}
                    >
                      {rowContent}
                    </View>
                  );
                }

                return (
                  <div
                    key={filter.id}
                    onDragOver={(e) => {
                      e.preventDefault();
                      e.dataTransfer.dropEffect = 'move';
                      setDragState((current) =>
                        current.over === index ? current : { ...current, over: index }
                      );
                    }}
                    onDrop={(e) => {
                      e.preventDefault();
                      const from = Number(e.dataTransfer.getData('text/plain'));
                      handleFilterDrop(Number.isFinite(from) ? from : null, index);
                    }}
                    style={{
                      borderTop: showTopBar
                        ? `2px solid ${ADMIN_COLORS.accent}`
                        : undefined,
                      borderBottom: showBottomBar
                        ? `2px solid ${ADMIN_COLORS.accent}`
                        : index < displayFilters.length - 1
                          ? `1px solid ${ADMIN_COLORS.border}`
                          : undefined,
                      opacity: isDragging ? 0.55 : 1,
                      transition: 'opacity 120ms ease',
                    }}
                  >
                    {rowContent}
                  </div>
                );
              })}
            </View>
          )}

          {reordering && (
            <Text style={styles.reorderHint}>Salvando nova ordem…</Text>
          )}

          {!showCreate && !isEditing && (
            <Pressable
              onPress={openCreateForm}
              disabled={saving}
              style={({ hovered }) => [
                styles.newFilterBtn,
                hovered && { backgroundColor: ADMIN_COLORS.accentSoft },
              ]}
            >
              <Ionicons name="add" size={18} color={ADMIN_COLORS.accent} />
              <Text style={styles.newFilterBtnText}>Novo filtro</Text>
            </Pressable>
          )}

          {(showCreate || isEditing) && (
            <View style={styles.formBlock}>
              <View style={styles.formHeader}>
                <Text style={styles.formTitle}>
                  {isEditing ? 'Editar filtro' : 'Novo filtro'}
                </Text>
                <Pressable
                  onPress={resetForm}
                  hitSlop={8}
                  style={({ hovered }) => [
                    styles.closeBtn,
                    hovered && { backgroundColor: ADMIN_COLORS.surfaceSubtle },
                  ]}
                >
                  <Ionicons name="close" size={18} color={ADMIN_COLORS.muted} />
                </Pressable>
              </View>

              <Text style={styles.fieldLabel}>Nome do filtro</Text>
              <AdminInput
                placeholder="Ex: Iniciante"
                value={newName.value}
                onChangeText={(text) => setNewName({ value: text, error: '' })}
              />
              {newName.error ? <Text style={styles.fieldError}>{newName.error}</Text> : null}

              <Text style={[styles.fieldLabel, { marginTop: 20 }]}>
                Módulos deste produto
              </Text>
              <Text style={styles.fieldHint}>
                Marcar a categoria seleciona todos os módulos dela neste produto.
              </Text>
              <View style={styles.treeBox}>{renderTree()}</View>

              {formError ? <Text style={styles.fieldError}>{formError}</Text> : null}

              <View style={styles.formActions}>
                <Pressable
                  onPress={resetForm}
                  style={({ hovered }) => [
                    styles.secondaryBtn,
                    hovered && { backgroundColor: ADMIN_COLORS.border },
                  ]}
                >
                  <Text style={styles.secondaryBtnText}>Cancelar</Text>
                </Pressable>
                <Pressable
                  onPress={handleSave}
                  disabled={saving}
                  style={({ hovered }) => [
                    styles.primaryBtn,
                    saving && { opacity: 0.6 },
                    hovered && !saving && { backgroundColor: ADMIN_COLORS.accentDark },
                  ]}
                >
                  {saving ? (
                    <ActivityIndicator size="small" color="#FFFFFF" />
                  ) : (
                    <Text style={styles.primaryBtnText}>Salvar filtro</Text>
                  )}
                </Pressable>
              </View>
            </View>
          )}
        </>
      )}
    </SectionCard>
  );
}

const styles = StyleSheet.create({
  loadingBox: { paddingVertical: 16, alignItems: 'center' },
  listBox: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    overflow: 'hidden',
    marginBottom: 12,
  },
  listDivider: { borderBottomWidth: 1, borderBottomColor: ADMIN_COLORS.border },
  filterRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 10,
    paddingVertical: 10,
    paddingHorizontal: 12,
  },
  reorderHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.muted,
    marginBottom: 8,
  },
  filterRowMain: {
    flex: 1,
    minWidth: 0,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    cursor: 'pointer',
  },
  filterName: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.ink,
  },
  expanded: {
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
    borderTopWidth: 1,
    borderTopColor: ADMIN_COLORS.border,
    padding: 14,
  },
  expandedLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: '700',
    color: ADMIN_COLORS.muted,
    marginBottom: 8,
  },
  expandedItem: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.ink,
    lineHeight: 20,
  },
  expandedEmpty: { fontFamily: ADMIN_FONT, fontSize: 13, color: ADMIN_COLORS.muted },
  expandedActions: { flexDirection: 'row', gap: 8, marginTop: 12 },
  ghostBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 12,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: 'pointer',
  },
  ghostBtnText: { fontFamily: ADMIN_FONT, fontSize: 13, fontWeight: '600', color: ADMIN_COLORS.ink },
  newFilterBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 8,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.accent,
    borderRadius: 10,
    paddingVertical: 12,
    cursor: 'pointer',
  },
  newFilterBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '600',
    color: ADMIN_COLORS.accent,
  },
  formBlock: {
    marginTop: 4,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 10,
    padding: 16,
    backgroundColor: ADMIN_COLORS.surfaceSubtle,
  },
  formHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  formTitle: {
    fontFamily: ADMIN_FONT,
    fontSize: 14,
    fontWeight: '700',
    color: ADMIN_COLORS.ink,
  },
  closeBtn: {
    width: 28,
    height: 28,
    borderRadius: 8,
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
  },
  fieldLabel: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.ink,
    marginBottom: 6,
  },
  fieldHint: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    lineHeight: 17,
    color: ADMIN_COLORS.muted,
    marginBottom: 8,
  },
  fieldError: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.danger,
    marginTop: 6,
  },
  treeBox: {
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 8,
    backgroundColor: ADMIN_COLORS.surface,
    padding: 8,
    maxHeight: 280,
    overflow: 'scroll',
  },
  emptyTree: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.muted,
    lineHeight: 18,
    padding: 8,
  },
  treeRow: { flexDirection: 'row', alignItems: 'center' },
  treeChevron: { padding: 4, cursor: 'pointer' },
  treeLabelBtn: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8,
    paddingVertical: 6,
    cursor: 'pointer',
  },
  treeCardRow: { paddingLeft: 28 },
  treeLabel: {
    flex: 1,
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    color: ADMIN_COLORS.ink,
  },
  formActions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 10,
    marginTop: 16,
  },
  secondaryBtn: {
    minHeight: 40,
    borderRadius: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.secondaryBg,
    cursor: 'pointer',
  },
  secondaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '600',
    color: ADMIN_COLORS.ink,
  },
  primaryBtn: {
    minHeight: 40,
    borderRadius: 8,
    paddingHorizontal: 16,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: ADMIN_COLORS.accent,
    cursor: 'pointer',
  },
  primaryBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 13,
    fontWeight: '600',
    color: '#FFFFFF',
  },
  generateWithAiBtn: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: ADMIN_COLORS.border,
    borderRadius: 8,
    paddingVertical: 8,
    paddingHorizontal: 10,
    backgroundColor: ADMIN_COLORS.surface,
    cursor: 'pointer',
  },
  generateWithAiBtnText: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    fontWeight: '600',
    color: ADMIN_COLORS.accent,
  },
  aiPromptFeedback: {
    fontFamily: ADMIN_FONT,
    fontSize: 12,
    color: ADMIN_COLORS.success,
    marginBottom: 10,
    lineHeight: 17,
  },
});
